import axios from "axios";
import { createPortal } from "react-dom";
import { FullScreenLoading } from "components/ui/loader";
import {
  createHoliday,
  deleteHoliday,
  getHolidayTypes,
  getHolidays,
  updateHoliday,
  HolidayTypeApi,
  HolidayFilters,
} from "../../services/holidayService";
import useDebounce from "hooks/useDebounce";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit3,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { toast } from "sonner";
import Pagination from "../../ui/pagination";
import { InputFieldset } from "../../ui/inputFieldset";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";

type Holiday = {
  holidayTypeId?: number;
  publicId: string;
  title: string;
  date: string;
  type: string;
  status: string;
};
type HolidayFormErrors = {
  title?: string;
  date?: string;
  type?: string;
};

type HolidaySortKey = "date" | "title" | "type" | "status";

export function HolidaysMasterPage() {
  const { setPageHeader } = usePageHeader();
  const [rows, setRows] = useState<Holiday[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<HolidaySortKey>("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [filters, setFilters] = useState({
    status: undefined as HolidayFilters["status"],
    pageNo: 1,
    pageSize: 20,
  });
  const mergedFilters = useMemo(
    () => ({ ...filters, searchTerm }),
    [filters, searchTerm],
  );
  const debouncedFilters = useDebounce(mergedFilters, 400);

  const [totalCount, setTotalCount] = useState(0);
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [form, setForm] = useState<Holiday>({
    publicId: "",
    title: "",
    date: "",
    type: "",
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState<HolidayFormErrors>({});
  const [holidayTypes, setHolidayTypes] = useState<HolidayTypeApi[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState(false);
  const [saving, setSaving] = useState(false);
  const savePending = useRef(false);
  const [holidayToDelete, setHolidayToDelete] = useState<Holiday | null>(null);
  const [deleting, setDeleting] = useState(false);
  const deletePending = useRef(false);

  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const loadHolidays = useCallback(async () => {
    try {
      setListLoading(true);
      setListError(null);
      const result = await getHolidays(debouncedFilters);
      setRows(
        (result?.data?.data || []).map((holiday) => ({
          publicId: holiday.publicId,
          title: holiday.holidayName,
          date: holiday.holidayDate.slice(0, 10),
          holidayTypeId: holiday.holidayTypeId,
          type: holiday.holidayTypeName,
          status: holiday.isActive ? "Active" : "Inactive",
        })),
      );
      setTotalCount(result?.data?.totalRecords || 0);
    } catch {
      setListError("Failed to load holidays. Please try again.");
      toast.error("Failed to load holidays. Please try again.");
    } finally {
      setListLoading(false);
    }
  }, [debouncedFilters]);

  useEffect(() => {
    void loadHolidays();
  }, [loadHolidays]);

  const handleDelete = async () => {
    if (!holidayToDelete || deletePending.current) return;
    deletePending.current = true;
    setDeleting(true);
    try {
      const response = await deleteHoliday(holidayToDelete.publicId);
      await loadHolidays();
      setHolidayToDelete(null);
      toast.success(response.message || "Holiday deleted successfully.");
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message ||
              "Failed to delete holiday. Please try again."
          : error instanceof Error
            ? error.message
            : "Failed to delete holiday.",
      );
    } finally {
      deletePending.current = false;
      setDeleting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    getHolidayTypes()
      .then((types) => {
        if (!cancelled) setHolidayTypes(types.filter((type) => type.isActive));
      })
      .catch(() => {
        if (!cancelled) {
          setTypesError(true);
          toast.error("Failed to load holiday types.");
        }
      })
      .finally(() => {
        if (!cancelled) setTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPageHeader({ title: "Holidays", icon: <Users className="h-5 w-5" /> });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const handleSort = (key: HolidaySortKey) => {
    if (sortKey === key)
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const renderSortIcon = (key: HolidaySortKey) =>
    sortKey === key ? (
      sortDirection === "asc" ? (
        <ArrowUp className="w-3 h-3" />
      ) : (
        <ArrowDown className="w-3 h-3" />
      )
    ) : (
      <ArrowUpDown className="w-3 h-3 opacity-30" />
    );

  const pageRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          String(a[sortKey] ?? "").localeCompare(
            String(b[sortKey] ?? ""),
            undefined,
            { numeric: true },
          ) * (sortDirection === "asc" ? 1 : -1),
      ),
    [rows, sortKey, sortDirection],
  );

  const openForm = (row?: Holiday) => {
    const value: Holiday = {
      publicId: row?.publicId ?? "",
      holidayTypeId: row?.holidayTypeId,
      title: row?.title ?? "",
      date: row?.date ?? "",
      type: String(row?.holidayTypeId ?? ""),
      status: row?.status ?? "Active",
    };
    setFormErrors({});
    setForm(value);
    setEditing(row || value);
  };

  const save = async () => {
    if (savePending.current) return;
    //validation
    const errors: HolidayFormErrors = {};
    if (!form.title.trim()) errors.title = "Enter a holiday name.";
    if (!form.date) {
      errors.date = "Choose a holiday date.";
    } else {
      const date = new Date(`${form.date}T00:00:00Z`);
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(form.date) ||
        Number.isNaN(date.getTime()) ||
        date.toISOString().slice(0, 10) !== form.date
      ) {
        errors.date = "Enter a valid holiday date.";
      }
    }
    const selectedType = holidayTypes.find(
      (type) => String(type.id) === form.type,
    );
    if (!form.type) errors.type = "Choose a holiday type.";
    else if (!selectedType) {
      errors.type = "Choose a valid holiday type.";
    }
    //validation end
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    if (!selectedType) return;
    // console.log("Holiday form values:", form);
    savePending.current = true;
    setSaving(true);
    try {
      const payload = {
        holidayName: form.title.trim(),
        holidayDate: `${form.date}T00:00:00Z`,
        holidayTypeId: selectedType.id,
        isActive: form.status === "Active",
      };
      const response = form.publicId
        ? await updateHoliday(form.publicId, payload)
        : await createHoliday(payload);
      setEditing(null);
      toast.success(response.message || "Holiday saved successfully.");
      await loadHolidays();
    } catch (error) {
      toast.error(
        axios.isAxiosError(error)
          ? error.response?.data?.message ||
              "Failed to save holiday. Please try again."
          : error instanceof Error
            ? error.message
            : "Failed to save holiday.",
      );
    } finally {
      savePending.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {(listLoading || typesLoading || saving || deleting) &&
        createPortal(
          <div className="fixed inset-0 z-[100]">
            <FullScreenLoading
              title={
                deleting
                  ? "Deleting holiday..."
                  : saving
                    ? "Saving holiday..."
                    : "Loading holidays..."
              }
            />
          </div>,
          document.body,
        )}
      <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setFilters((current) => ({ ...current, pageNo: 1 }));
              }}
              placeholder="Search by holiday title"
              className="h-10 pl-9 pr-9 shadow-sm"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  setFilters((current) => ({ ...current, pageNo: 1 }));
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            className="text-white"
            style={{ background: "#5B9BD5" }}
            onClick={() => openForm()}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Holiday
          </Button>
        </div>
      </div>

      {/* TABLE START */}
      <Card
        className="bg-white border-[#5B9BD5]/30"
        style={{ gap: "0px", marginTop: "10px" }}
      >
        <CardContent style={{ padding: "0px 0px 10px 0px" }}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="whitespace-nowrap px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("date")}
                  >
                    <div className="flex items-center gap-1">
                      Date
                      {renderSortIcon("date")}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="whitespace-nowrap px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("title")}
                  >
                    <div className="flex items-center gap-1">
                      Holiday Title
                      {renderSortIcon("title")}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="whitespace-nowrap px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center gap-1">
                      Type
                      {renderSortIcon("type")}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="whitespace-nowrap px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {renderSortIcon("status")}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-right"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!listLoading &&
                  !listError &&
                  pageRows.map((row) => (
                    <TableRow key={row.publicId} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-3 text-xs text-gray-700">
                        <div className="flex items-center gap-2">
                          <span>
                            {new Date(
                              `${row.date}T00:00:00`,
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-700">
                        {row.title}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-700">
                        {row.type}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-xs text-gray-700">
                        <Badge
                          className={
                            row.status === "Active"
                              ? "border border-emerald-300 bg-emerald-50 text-emerald-700"
                              : "border border-rose-300 bg-rose-50 text-rose-700"
                          }
                        >
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-[#5B9BD5] hover:bg-sky-50"
                            onClick={() => openForm(row)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={() => setHolidayToDelete(row)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                {(listLoading || listError || pageRows.length === 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 px-4 text-[#5B9BD5] font-medium"
                    >
                      {listLoading ? (
                        "Loading holidays..."
                      ) : listError ? (
                        <div role="alert">
                          {listError}
                          <Button
                            variant="outline"
                            className="ml-3"
                            onClick={() => void loadHolidays()}
                          >
                            Retry
                          </Button>
                        </div>
                      ) : (
                        "No holidays found."
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        {!listLoading && !listError && totalCount > 0 && (
          <Pagination
            totalItems={totalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((current) => ({ ...current, pageNo: page }))
            }
            onPageSizeChange={(size) => {
              setFilters((current) => ({
                ...current,
                pageSize: size,
                pageNo: 1,
              }));
            }}
            label="holidays"
          />
        )}
      </Card>
      <ConfirmDialog
        open={!!holidayToDelete}
        onOpenChange={(open) => {
          if (!open && !deletePending.current) setHolidayToDelete(null);
        }}
        title="Delete Holiday"
        description={`Are you sure you want to delete "${holidayToDelete?.title || "this holiday"}"?`}
        confirmText="Delete Holiday"
        onConfirm={handleDelete}
        loading={deleting}
        show={false}
      />
      {/* TABLE END */}

      {/* ADD/Edit Dialog */}
      <Dialog
        open={!!editing}
        onOpenChange={(open) => !open && !saving && setEditing(null)}
      >
        <DialogContent className="w-[calc(100vw-2rem)] !max-w-[calc(100vw-2rem)] sm:w-[60vw] sm:!max-w-[60vw] max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              {!!form.publicId ? "Edit" : "Add New"} Holiday
            </DialogTitle>
            <DialogDescription>
              Fill in details to add a new holiday.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 px-1 py-4 sm:grid-cols-2">
            <div className="min-w-0">
              <InputFieldset
                disabled={saving}
                label="Holiday Name"
                required
                type="text"
                value={form.title}
                onChange={(e) => {
                  setForm((current) => ({ ...current, title: e.target.value }));
                  setFormErrors((current) => ({
                    ...current,
                    title: undefined,
                  }));
                }}
                aria-invalid={!!formErrors.title}
                aria-describedby={
                  formErrors.title ? "holiday-title-error" : undefined
                }
                placeholder="Enter Holiday Name"
              />
              {formErrors.title && (
                <p
                  id="holiday-title-error"
                  role="alert"
                  className="mt-1 text-xs text-red-500"
                >
                  {formErrors.title}
                </p>
              )}
            </div>
            <div className="min-w-0">
              <InputFieldset
                disabled={saving}
                label="Choose Date"
                required
                type="date"
                value={form.date}
                onChange={(e) => {
                  setForm((current) => ({ ...current, date: e.target.value }));
                  setFormErrors((current) => ({ ...current, date: undefined }));
                }}
                aria-invalid={!!formErrors.date}
                aria-describedby={
                  formErrors.date ? "holiday-date-error" : undefined
                }
              />
              {formErrors.date && (
                <p
                  id="holiday-date-error"
                  role="alert"
                  className="mt-1 text-xs text-red-500"
                >
                  {formErrors.date}
                </p>
              )}
            </div>
            <div className="min-w-0">
              <SelectFieldset label="Holiday Type" required>
                <Select
                  value={form.type}
                  disabled={typesLoading || !holidayTypes.length || saving}
                  onValueChange={(value) => {
                    setForm((current) => ({ ...current, type: value }));
                    setFormErrors((current) => ({
                      ...current,
                      type: undefined,
                    }));
                  }}
                >
                  <SelectTriggerUI
                    aria-invalid={!!formErrors.type}
                    aria-describedby={
                      formErrors.type ? "holiday-type-error" : undefined
                    }
                  >
                    <SelectValue
                      placeholder={
                        typesLoading
                          ? "Loading holiday types..."
                          : typesError
                            ? "Unable to load holiday types"
                            : !holidayTypes.length
                              ? "No holiday types available"
                              : "Choose Holiday Type"
                      }
                    />
                  </SelectTriggerUI>
                  <SelectContent>
                    {holidayTypes.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.typeName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SelectFieldset>
              {formErrors.type && (
                <p
                  id="holiday-type-error"
                  role="alert"
                  className="mt-1 text-xs text-red-500"
                >
                  {formErrors.type}
                </p>
              )}
            </div>
            <div className="min-w-0 flex flex-col justify-center gap-2 px-3 py-2">
              <label
                htmlFor="holiday-status"
                className="text-sm font-medium text-gray-700"
              >
                Status
              </label>
              <div className="flex items-center gap-3">
                <Switch
                  id="holiday-status"
                  disabled={saving}
                  checked={form.status === "Active"}
                  onCheckedChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      status: checked ? "Active" : "Inactive",
                    }))
                  }
                />
                <span className="text-sm text-gray-700">{form.status}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 bg-white border-t sm:border-0 px-6 py-4 sm:p-0 sm:static flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setEditing(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              style={{ background: "#5B9BD5" }}
              className="text-white w-full sm:w-auto"
              disabled={saving || typesLoading || !holidayTypes.length}
              onClick={save}
            >
              {saving
                ? "Saving..."
                : !!form.publicId
                  ? "Update Holiday"
                  : "Add Holiday"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
